/**
 * AdminQuestReview.jsx — admin queue for pending quest submissions
 * (iter 64). Lists every user_quest_progress row in approval_status
 * 'pending' with the submitter's text/link/notes and an Approve / Reject
 * button. Polls every 30s so the admin sees new completions without
 * refreshing.
 *
 * Routed at /admin/quest-review (admin-only).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Trophy, Loader2, CheckCircle2, XCircle, FileText, Link as LinkIcon,
  StickyNote, Hourglass, User as UserIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const ReviewCard = ({ row, onReview }) => {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const quest = row.quest || {};
  const user = row.user || {};
  const params = quest.params || {};
  const userLabel = user.username || user.email || user.user_id || 'Unknown user';

  const review = async (decision) => {
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/quests/${row.quest_id}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: row.user_id, decision, notes }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Review failed');
      toast.success(decision === 'approve' ? 'Approved' : 'Rejected');
      onReview?.();
    } catch (e) {
      toast.error(typeof e?.message === 'string' ? e.message : 'Error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      className="rounded-xl border border-amber-400/30 bg-slate-900/60 p-4 space-y-3"
      data-testid={`review-card-${row.quest_id}-${row.user_id}`}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-bold text-white truncate">{quest.title || row.quest_id}</h3>
          <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
            <UserIcon className="w-3.5 h-3.5" />
            <span className="font-mono truncate">{userLabel}</span>
            <span>·</span>
            <span>Submitted {formatDate(row.submitted_at)}</span>
          </div>
          {(params.min_word_count || params.max_word_count) && (
            <p className="mt-1 text-[11px] text-indigo-300 font-bold uppercase tracking-wider">
              Word target: min {params.min_word_count || 0}
              {params.max_word_count ? ` · cap ${params.max_word_count}` : ''}
              {' · '}submitted {row.submission_word_count || 0} words
              {params.min_word_count && row.submission_word_count >= params.min_word_count ? ' ✓' : ''}
            </p>
          )}
        </div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-200 text-[10px] font-extrabold uppercase tracking-wider flex-shrink-0">
          <Hourglass className="w-3 h-3" /> Pending
        </span>
      </header>

      {row.submission_text && (
        <div className="rounded-md border border-slate-700 bg-slate-900/80 p-3">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">
            <FileText className="w-3 h-3" /> Submitted text
          </div>
          <pre className="text-xs text-slate-200 whitespace-pre-wrap font-sans max-h-60 overflow-auto">
{row.submission_text}
          </pre>
        </div>
      )}
      {row.submission_link && (
        <a
          href={row.submission_link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-sky-300 hover:underline break-all"
        >
          <LinkIcon className="w-3 h-3" /> {row.submission_link}
        </a>
      )}
      {row.submission_notes && (
        <div className="text-xs text-slate-300 inline-flex items-start gap-1">
          <StickyNote className="w-3 h-3 flex-shrink-0 mt-0.5 text-amber-300" />
          <span className="whitespace-pre-wrap">{row.submission_notes}</span>
        </div>
      )}

      <input
        type="text"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Reviewer notes (sent to user on reject)"
        className="w-full bg-slate-900/80 border border-slate-700 rounded-md px-2 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-400"
        data-testid={`review-notes-${row.quest_id}-${row.user_id}`}
      />

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => review('approve')}
          disabled={busy}
          className="flex-1 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white text-sm font-bold inline-flex items-center justify-center gap-1"
          data-testid={`review-approve-${row.quest_id}-${row.user_id}`}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Approve
        </button>
        <button
          type="button"
          onClick={() => review('reject')}
          disabled={busy}
          className="flex-1 px-3 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 disabled:bg-slate-700 text-white text-sm font-bold inline-flex items-center justify-center gap-1"
          data-testid={`review-reject-${row.quest_id}-${row.user_id}`}
        >
          <XCircle className="w-4 h-4" /> Reject
        </button>
      </div>
    </article>
  );
};

const AdminQuestReview = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchPending = useCallback(async () => {
    setError('');
    try {
      const r = await authFetch(`${BACKEND_URL}/api/quests/admin/pending`);
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(typeof d.detail === 'string' ? d.detail : `HTTP ${r.status}`);
      }
      const d = await r.json();
      setRows(d.pending || []);
    } catch (e) {
      setError(typeof e?.message === 'string' ? e.message : 'Failed to load review queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPending();
    // Poll every 30s — admin doesn't need to manually refresh as new
    // completions come in.
    const t = setInterval(fetchPending, 30_000);
    return () => clearInterval(t);
  }, [fetchPending]);

  return (
    <main className="max-w-3xl mx-auto px-4 py-6" data-testid="admin-quest-review-page">
      <header className="mb-5">
        <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
          <Trophy className="w-6 h-6 text-amber-400" /> Quest Review
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          {rows.length === 0
            ? 'No pending submissions right now.'
            : `${rows.length} ${rows.length === 1 ? 'submission' : 'submissions'} waiting for your call.`}
        </p>
      </header>

      {loading && (
        <div className="text-center text-slate-400 text-sm py-8">
          <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" /> Loading…
        </div>
      )}
      {!loading && error && (
        <div className="rounded-xl border border-rose-400/40 bg-rose-500/10 p-4 text-sm text-rose-200" data-testid="admin-review-error">
          {error}
        </div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div className="rounded-xl bg-slate-800/50 border border-slate-700 p-8 text-center text-sm text-slate-400" data-testid="admin-review-empty">
          Nothing waiting — you&apos;re caught up.
        </div>
      )}
      <div className="space-y-3" data-testid="admin-review-list">
        {rows.map((row) => (
          <ReviewCard
            key={`${row.quest_id}-${row.user_id}`}
            row={row}
            onReview={fetchPending}
          />
        ))}
      </div>
    </main>
  );
};

export default AdminQuestReview;
